import { z } from 'zod';
import { RANK_CATEGORIES } from '../constants/identity.constant';
import { createPaginationSchema, objectIdSchema } from './common.schema';

/**
 * The rank master (PA_REQUIREMENTS Appendix A, §4.2).
 *
 * `seniorityOrder` is an integer where lower is more senior, and it is deliberately
 * NOT UNIQUE: 80 is shared by Sub-Inspector, Sergeant and Subedar, and 100 by the two
 * constable rows. Equal values denote equivalent ranks and are the whole reason this is
 * an integer in a collection rather than an enum in code.
 */
export const rankBaseSchema = z.object({
  name: z.string().trim().min(1, 'Rank name is required').max(120),
  shortName: z.string().trim().min(1, 'Short name is required').max(24),
  seniorityOrder: z.number().int().min(1).max(1_000),
  category: z.enum(RANK_CATEGORIES),
  isGazetted: z.boolean(),
});

export const rankSchema = rankBaseSchema.extend({
  id: objectIdSchema,
  /**
   * ⚠️ [amended C1f §8b] ON THE WIRE BECAUSE THE PICKER NEEDS IT, and on `rankSchema` alone.
   *
   * `rankSummarySchema` below is embedded in every officer payload and has no consumer for
   * this; widening it would put a field on the officer contract for a policy screen.
   */
  isCommandEligible: z.boolean(),
  /**
   * ─── ⚠️ [C2b §5g] THE CLIENT'S OWN ABBREVIATION, ON THE WIRE FOR THE GROUP EXPANSION ──────
   *
   *  A rank GROUP declares CODES — `ranks.code` is the `RANK_OF` printed on the client's own
   *  posting orders, and [RANKS] settled that it is the only handle that survives a reseed or
   *  a re-import. So the browser cannot expand `{group: AC}` into rank ids without it.
   *
   *  ⚠️ NULLABLE, BECAUSE MOST ROWS HAVE NONE. The partial unique index [RANKS] records exists
   *  precisely for that: a code is absent on every row the client never named, and a row with
   *  no code simply cannot be a group member.
   *
   *  ⚠️ AND ON `rankSchema` ALONE, for `isCommandEligible`'s reason one line up:
   *  `rankSummarySchema` is embedded in every officer payload and has no consumer for this.
   *  Widening it would put a field on the officer contract for a policy screen.
   *
   *  ⚠️ IT DISCLOSES NOTHING. The abbreviation is printed on every posting order and is
   *  already on every rank's `shortName` in spirit — `place.read`'s argument, one master over.
   */
  code: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

/**
 * Every value here has an index on the model (CLAUDE.md §7). `createdAt` is absent on
 * purpose: it would cost a fifth index on a fourteen-row master to serve a sort nobody
 * performs.
 */
export const RANK_SORT_FIELDS = ['seniorityOrder', 'name', 'shortName'] as const;
export type RankSortField = (typeof RANK_SORT_FIELDS)[number];

export const listRanksQuerySchema = createPaginationSchema(RANK_SORT_FIELDS, 'seniorityOrder')
  .extend({
    category: z.enum(RANK_CATEGORIES).optional(),
    isActive: z.stringbool().optional(),
    search: z.string().trim().min(1).max(120).optional(),
  })
  // The picker wants seniority ascending; a `desc` default inherited from the base
  // pagination schema would list constables first.
  .extend({ sortOrder: z.enum(['asc', 'desc']).default('asc') });

/** Embedded in an officer payload so a list page needs no second round trip. */
export const rankSummarySchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  shortName: z.string(),
  seniorityOrder: z.number().int(),
});
